import type { ModalFuncProps } from "../../../../../../../shared/frontend/ui";
import type { ReactNode } from "react";
import type { ApiClient } from "../../../app/api/client";
import type { I18nContextValue } from "../../../app/i18n/useI18n";
import { emitPersonsChanged } from "../../../app/persons/personsEvents";
import type { PersonDto } from "../types";

type MessageApi = {
  success: (content: ReactNode) => unknown;
};

type ConfirmModalApi = {
  confirm: (config: ModalFuncProps) => unknown;
};

type ReassignAccessMediumArgs = {
  api: ApiClient;
  personId: string;
  accountId?: string;
  mediumId: string;
  message: MessageApi;
  text: I18nContextValue["text"];
  onDone: () => void;
};

type SetManualCheckInBlockArgs = {
  api: ApiClient;
  modal: ConfirmModalApi;
  message: MessageApi;
  personId: string;
  wantBlock: boolean;
  text: I18nContextValue["text"];
  refreshPerson: () => Promise<PersonDto | null>;
  onPersonUpdated: (person: PersonDto | null) => void;
};

function confirmManualCheckInBlock(modal: ConfirmModalApi, text: I18nContextValue["text"]) {
  return new Promise<boolean>((resolve) => {
    modal.confirm({
      title: text("Manual check-in block confirm title"),
      content: text("Manual check-in block confirm content"),
      okText: text("Manual check-in block confirm ok"),
      okButtonProps: { danger: true },
      onOk: () => resolve(true),
      onCancel: () => resolve(false)
    });
  });
}

export async function reassignAccessMedium({
  api,
  personId,
  accountId,
  mediumId,
  message,
  text,
  onDone
}: ReassignAccessMediumArgs) {
  await api.post("/api/v1/access-media/reassign", {
    personId: accountId ? null : personId,
    accountId: accountId || null,
    mediumId
  });
  message.success(text("Access medium reassigned."));
  onDone();
}

export async function setManualCheckInBlock({
  api,
  modal,
  message,
  personId,
  wantBlock,
  text,
  refreshPerson,
  onPersonUpdated
}: SetManualCheckInBlockArgs) {
  if (wantBlock) {
    const ok = await confirmManualCheckInBlock(modal, text);
    if (!ok) return;
  }

  await api.post(`/api/v1/persons/${encodeURIComponent(personId)}/manual-checkin-block`, {
    isBlocked: wantBlock
  });
  message.success(text(wantBlock ? "Access blocked" : "Access block removed"));
  emitPersonsChanged({ reason: "updated", personId: String(personId) });
  const updatedPerson = await refreshPerson();
  onPersonUpdated(updatedPerson);
}
